import { createSlice } from '@reduxjs/toolkit';
import { createApi } from '@reduxjs/toolkit/query/react';
import { useSelector } from 'react-redux';

import { rootReducer } from '../store';
import { baseQuery } from './auth';

export const helpCategoriesApi = createApi({
  reducerPath: 'helpCategoriesApi',
  baseQuery: baseQuery(),
  endpoints: (builder) => ({
    getHelpCategories: builder.query<HelpCategoriesResponse, void>({
      query: () => ({
        url: 'help-categories',
        method: 'GET',
        params: {
          sort: 'id',
        },
      }),
    }),
    getHelpArticlesByCategorySlug: builder.query<HelpArticlesResponse, HelpArticlesByCategorySlugParams>({
      query: ({ slug, search, page }) => {
        const params = [
          slug ? `filters[helpCategory][slug]=${encodeURIComponent(slug)}` : '',
          search ? `filters[%24or][0][title][%24containsi]=${encodeURIComponent(search)}` : '',
          search ? `filters[%24or][1][subtitle][%24containsi]=${encodeURIComponent(search)}` : '',
          page ? `pagination[page]=${page}` : '',
        ].filter(Boolean);

        return {
          url: `help-articles${params.length ? `?${params.join('&')}` : ''}`,
          method: 'GET',
        };
      },
    }),
    getLatestHelpArticles: builder.query<HelpArticlesResponse, void>({
      query: () => ({
        url: 'help-articles?sort=publishedAt:desc&populate=helpCategory',
        method: 'GET',
      }),
    }),
    getHelpArticle: builder.query<HelpArticleResponse, string>({
      query: (documentId) => ({
        url: `help-articles/${documentId}?populate=*`,
        method: 'GET',
      }),
    }),
  }),
});

export const helpCategoriesSlice = createSlice({
  name: 'helpCategories',
  initialState: {
    categories: [] as HelpCategory[],
    categoriesMeta: null as HelpCategoriesResponse['meta'] | null,
    categoriesLoaded: false,
    articlesByCategorySlug: {} as Record<string, HelpArticlesResponse>,
    helpArticle: null as HelpArticleResponse | null,
    helpArticleLoaded: false,
  },
  reducers: {},
  extraReducers: (builder) => {
    builder.addMatcher(
      helpCategoriesApi.endpoints.getHelpCategories.matchFulfilled,
      (state, { payload }) => {
        state.categories = payload.data;
        state.categoriesMeta = payload.meta;
        state.categoriesLoaded = true;
      },
    );
    builder.addMatcher(
      helpCategoriesApi.endpoints.getHelpArticlesByCategorySlug.matchFulfilled,
      (state, { payload, meta }) => {
        state.articlesByCategorySlug[meta.arg.originalArgs.slug || 'search'] = payload;
      },
    );
    builder.addMatcher(
      helpCategoriesApi.endpoints.getHelpArticle.matchFulfilled,
      (state, { payload }) => {
        state.helpArticle = payload;
        state.helpArticleLoaded = true;
      },
    );
  },
});

export const {
  useGetHelpArticleQuery,
  useGetHelpArticlesByCategorySlugQuery,
  useGetHelpCategoriesQuery,
  useGetLatestHelpArticlesQuery,
  useLazyGetHelpArticleQuery,
  useLazyGetHelpArticlesByCategorySlugQuery,
  useLazyGetHelpCategoriesQuery,
  useLazyGetLatestHelpArticlesQuery,
} = helpCategoriesApi;

export const useHelpCategories = () => {
  return useSelector(({ helpCategories }: ReturnType<typeof rootReducer>) => (
    helpCategories?.categories || []
  ));
};

export const useHelpCategoriesLoaded = () => {
  return useSelector(({ helpCategories }: ReturnType<typeof rootReducer>) => (
    Boolean(helpCategories?.categoriesLoaded)
  ));
};

export const useHelpArticleLoaded = () => {
  return useSelector(({ helpCategories }: ReturnType<typeof rootReducer>) => (
    Boolean(helpCategories?.helpArticleLoaded)
  ));
};
